import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { PlusCircle, BellOff, Bell, Pin, LogOut } from 'lucide-react';
import { translateEnglishDays } from '../../../../utils';
import { auth } from '../../../../config/firebaseConfig';
import { apiPost, apiDelete } from '../../../../utils/apiClient';

const LEAVE_W = 80;
const ACTION_W = 160;

function loadLS(key, def) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; }
}
function saveLS(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

const formatLastMessageTime = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return '방금';
  if (diffMin < 60) return `${diffMin}분 전`;
  if (diffHour < 24) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

/* ── 스와이프 가능한 방 카드 ── */
const SwipeableRoomCard = ({
  room, exchangeCount,
  isPinned, isMuted, onPin, onMute, onLeaveRoom,
  openCardId, setOpenCardId,
  onClick, aliasVersion,
}) => {
  const [offset, setOffset]   = useState(0);
  const [noTrans, setNoTrans] = useState(false);

  // 나만 보이는 방 이름 별칭 + 멤버 닉네임 치환 계산
  const calcRoomDisplayName = () => {
    try {
      const uid = auth.currentUser?.uid || '';
      // 1) 로컬 별칭 우선
      const alias = uid ? localStorage.getItem(`roomNameAlias_${room._id}_${uid}`) || '' : '';
      if (alias) return alias;
      // 2) 별칭 없으면 멤버 닉네임으로 방이름 치환
      const localNicknames = (() => {
        try { return JSON.parse(localStorage.getItem(`roomLocalNicknames_${room._id}_${uid}`) || '{}'); }
        catch { return {}; }
      })();
      let name = room.name || '';
      (room.members || []).forEach(m => {
        const memberId = (m.user?._id || m.user?.id || m.user)?.toString();
        const nickname = localNicknames[memberId] || m.nickname;
        if (nickname) {
          const userData = m.user;
          const realName = typeof userData === 'object'
            ? [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim()
            : '';
          if (realName && name.includes(realName)) {
            name = name.split(realName).join(nickname);
          }
        }
      });
      return name;
    } catch { return room.name || ''; }
  };
  const [roomDisplayName, setRoomDisplayName] = useState(calcRoomDisplayName);
  useEffect(() => {
    setRoomDisplayName(calcRoomDisplayName());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliasVersion, room._id, room.name]);

  const containerRef = useRef(null);
  const offsetRef    = useRef(0);
  const baseOffset   = useRef(0);
  const startX       = useRef(null);
  const startY       = useRef(null);
  const isHoriz      = useRef(false);
  const wasMoved     = useRef(false);

  /* 다른 카드가 열리면 이 카드 닫기 */
  useEffect(() => {
    if (openCardId !== room._id && offsetRef.current !== 0) {
      offsetRef.current = 0;
      setNoTrans(false);
      setOffset(0);
    }
  }, [openCardId, room._id]);

  const snapTo = useCallback((target) => {
    offsetRef.current = target;
    setNoTrans(false);
    setOffset(target);
    if (target !== 0) setOpenCardId(room._id);
    else setOpenCardId(prev => (prev === room._id ? null : prev));
  }, [room._id, setOpenCardId]);

  /* non-passive touchmove */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleMove = (e) => {
      if (startX.current === null) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = e.touches[0].clientY - startY.current;
      if (!isHoriz.current) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx)) { startX.current = null; return; }
        isHoriz.current = true;
        setNoTrans(true);
      }
      e.preventDefault();
      wasMoved.current = true;
      const newOffset = Math.max(-LEAVE_W, Math.min(ACTION_W, baseOffset.current + dx));
      offsetRef.current = newOffset;
      setOffset(newOffset);
    };
    el.addEventListener('touchmove', handleMove, { passive: false });
    return () => el.removeEventListener('touchmove', handleMove);
  }, []);

  const onTouchStart = (e) => {
    startX.current     = e.touches[0].clientX;
    startY.current     = e.touches[0].clientY;
    baseOffset.current = offsetRef.current;
    isHoriz.current    = false;
    wasMoved.current   = false;
  };

  const onTouchEnd = () => {
    startX.current = null;
    if (!wasMoved.current) return;
    const cur    = offsetRef.current;
    const target = cur > 40 ? ACTION_W : cur < -40 ? -LEAVE_W : 0;
    snapTo(target);
  };

  const handleClick = () => {
    if (wasMoved.current) return;
    if (offsetRef.current !== 0) { snapTo(0); return; }
    onClick();
  };

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden"
      style={{ touchAction: 'pan-y' }}
    >
      {/* 왼쪽: 알림끄기 + 고정 */}
      <div className="absolute inset-y-0 left-0 flex" style={{ width: ACTION_W }}>
        <button
          className={`flex-1 flex flex-col items-center justify-center text-white text-xs font-semibold gap-1 transition-colors
            ${isMuted ? 'bg-yellow-400 hover:bg-yellow-500' : 'bg-gray-500 hover:bg-gray-600'}`}
          onClick={(e) => { e.stopPropagation(); onMute(room._id); snapTo(0); }}
        >
          {isMuted ? <Bell size={18} /> : <BellOff size={18} />}
          <span>{isMuted ? '알림 켜기' : '알림 끄기'}</span>
        </button>
        <button
          className={`flex-1 flex flex-col items-center justify-center text-white text-xs font-semibold gap-1 transition-colors
            ${isPinned ? 'bg-orange-400 hover:bg-orange-500' : 'bg-blue-500 hover:bg-blue-600'}`}
          onClick={(e) => { e.stopPropagation(); onPin(room._id); snapTo(0); }}
        >
          <Pin size={18} />
          <span>{isPinned ? '고정 해제' : '고정'}</span>
        </button>
      </div>

      {/* 오른쪽: 나가기 */}
      <div className="absolute inset-y-0 right-0" style={{ width: LEAVE_W }}>
        <button
          className="w-full h-full flex flex-col items-center justify-center bg-red-500 hover:bg-red-600 text-white text-xs font-semibold gap-1 transition-colors"
          onClick={(e) => { e.stopPropagation(); if (onLeaveRoom) onLeaveRoom(room); snapTo(0); }}
        >
          <LogOut size={18} />
          <span>나가기</span>
        </button>
      </div>

      {/* 슬라이딩 카드 본체 */}
      <div
        className="bg-white px-4 py-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors relative z-10"
        style={{
          transform: `translateX(${offset}px)`,
          transition: noTrans ? 'none' : 'transform 0.25s cubic-bezier(0.25,0.46,0.45,0.94)',
        }}
        onClick={handleClick}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex items-center gap-3">
          {/* 아이콘 (방 이니셜) */}
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600 font-bold text-lg relative">
            {translateEnglishDays(roomDisplayName).charAt(0)}
            {isPinned && (
              <span className="absolute -top-0.5 -right-0.5 bg-blue-500 rounded-full p-0.5">
                <Pin size={8} className="text-white" />
              </span>
            )}
          </div>

          {/* 메인 콘텐츠 */}
          <div className="flex-1 min-w-0">
            {/* 첫줄: 방 이름 + 인원 + 뮤트 | 시간 */}
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-bold text-gray-900 truncate">{translateEnglishDays(roomDisplayName)}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">{room.memberCount || room.members?.length || 0}</span>
                {isMuted && <BellOff size={11} className="text-gray-300 flex-shrink-0" />}
              </div>
              <span className="text-[11px] text-gray-400 flex-shrink-0 ml-2">
                {formatLastMessageTime(room.lastMessageAt || room.createdAt)}
              </span>
            </div>

            {/* 둘째줄: 마지막 메시지 미리보기 | 배지 */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 truncate flex-1 mr-2">
                {room.lastMessagePreview || ''}
              </p>
              <div className="flex items-center gap-1 flex-shrink-0">
                {exchangeCount > 0 && (
                  <span className="bg-orange-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                    {exchangeCount}
                  </span>
                )}
                {room.unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                    {room.unreadCount}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyRoomState = () => (
  <div className="text-center py-16 bg-white rounded-lg shadow-md border">
    <div className="text-gray-400 text-8xl mb-6">📅</div>
    <h3 className="text-2xl font-bold text-gray-700 mb-4">시간표 조율을 시작해보세요!</h3>
    <p className="text-gray-500 mb-8 max-w-md mx-auto">
      팀 프로젝트나 스터디 그룹의 시간을 효율적으로 조율할 수 있습니다.
      방을 만들거나 기존 방에 참여해보세요.
    </p>
  </div>
);

const RoomList = ({
  myRooms,
  selectedTab,
  setSelectedTab,
  roomExchangeCounts,
  onRoomClick,
  onCreateRoom,
  onLeaveRoom,
  hideHeader = false,
}) => {
  const [pinnedIds, setPinnedIds] = useState(() => loadLS('pinnedRooms', []));
  const [mutedIds,  setMutedIds]  = useState(() => loadLS('mutedRooms',  []));
  const [openCardId, setOpenCardId] = useState(null);
  // 방 이름 별칭이 변경될 때 카드들을 강제 갱신하기 위한 버전 카운터
  const [aliasVersion, setAliasVersion] = useState(0);
  useEffect(() => {
    const handler = () => setAliasVersion(v => v + 1);
    window.addEventListener('roomAliasChanged', handler);
    return () => window.removeEventListener('roomAliasChanged', handler);
  }, []);

  const handlePin = useCallback((roomId) => {
    setPinnedIds(prev => {
      const next = prev.includes(roomId)
        ? prev.filter(id => id !== roomId)
        : [roomId, ...prev];
      saveLS('pinnedRooms', next);
      return next;
    });
  }, []);

  const handleMute = useCallback(async (roomId) => {
    setMutedIds(prev => {
      const isMuted = prev.includes(roomId);
      const next = isMuted
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId];
      saveLS('mutedRooms', next);
      // 서버 DB 동기화 (push 알림 차단 적용)
      if (isMuted) {
        apiDelete(`/api/push/mute/${roomId}`).catch(() => {});
      } else {
        apiPost(`/api/push/mute/${roomId}`, {}).catch(() => {});
      }
      return next;
    });
  }, []);

  const allRooms = useMemo(() => {
    const combined = [
      ...(myRooms?.owned || []),
      ...(myRooms?.joined || []),
    ].sort((a, b) => {
      const ta = new Date(a.lastMessageAt || a.createdAt || 0).getTime();
      const tb = new Date(b.lastMessageAt || b.createdAt || 0).getTime();
      return tb - ta;
    });

    const pinned   = combined
      .filter(r => pinnedIds.includes(r._id))
      .sort((a, b) => pinnedIds.indexOf(a._id) - pinnedIds.indexOf(b._id));
    const unpinned = combined.filter(r => !pinnedIds.includes(r._id));
    return [...pinned, ...unpinned];
  }, [myRooms, pinnedIds]);

  const hasRooms = allRooms.length > 0;

  return (
    <div className="bg-white min-h-full">
      {!hideHeader && (
        <div className="flex justify-between items-center mb-6 px-4 pt-4">
          <h2 className="text-2xl font-bold text-gray-800">일정 맞추기</h2>
          <button
            onClick={onCreateRoom}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm flex items-center gap-2"
          >
            <PlusCircle size={16} />
            그룹 만들기
          </button>
        </div>
      )}

      {hasRooms && (
        <div className="divide-y divide-gray-100">
          {allRooms.map(room => (
            <SwipeableRoomCard
              key={room._id}
              room={room}
              exchangeCount={roomExchangeCounts?.[room._id]}
              isPinned={pinnedIds.includes(room._id)}
              isMuted={mutedIds.includes(room._id)}
              onPin={handlePin}
              onMute={handleMute}
              onLeaveRoom={onLeaveRoom}
              openCardId={openCardId}
              setOpenCardId={setOpenCardId}
              onClick={() => onRoomClick(room)}
              aliasVersion={aliasVersion}
            />
          ))}
        </div>
      )}

      {!hasRooms && <EmptyRoomState />}
    </div>
  );
};

export default RoomList;
